import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Hover intent tuning.
 *
 * The mega menu spans the full page width, so its columns extend to the left and right of the
 * top-level link that opens it. A pointer travelling into the panel unavoidably crosses neighbouring
 * top-level links on the way, and a link without a submenu (Home, All products) would otherwise
 * collapse the panel outright.
 *
 * A pointer still in motion is treated as travelling, so switching panels waits for it to come to
 * rest on the new item. There is deliberately no upper bound on that wait: a pointer crawling
 * across a neighbouring link for two seconds is still travelling, and force-committing the switch
 * is exactly what blanked the panel mid-journey.
 *
 * Rest is "no `pointermove` for `SWITCH_REST`". A distance threshold would impose a minimum speed
 * below which travel reads as standing still, and a slow enough drag always slips under it; a still
 * pointer, by contrast, emits no move events at all, so this needs no threshold to tune.
 */
const SWITCH_REST = 130;
const CLOSE_DELAY = 220;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * A queued panel switch, waiting for the pointer to come to rest on `target`.
   * @type {{ target: Element } | null}
   */
  #pendingSwitch = null;

  /**
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #switchTimeout = null;

  /**
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #deactivateTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointerleave', this.#hostLeaveListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerleave', this.#hostLeaveListener);
    this.#cleanupMutationObserver();
    this.#clearPendingSwitch();
    this.#clearDeactivateTimeout();
  }

  #clearPendingSwitch() {
    if (this.#switchTimeout !== null) {
      clearTimeout(this.#switchTimeout);
      this.#switchTimeout = null;
    }

    if (this.#pendingSwitch) {
      this.#pendingSwitch = null;
      document.removeEventListener('pointermove', this.#pointerMoveListener);
    }
  }

  #clearDeactivateTimeout() {
    if (this.#deactivateTimeout === null) return;
    clearTimeout(this.#deactivateTimeout);
    this.#deactivateTimeout = null;
  }

  /**
   * While a switch is queued, a pointer that is still moving is still travelling towards the open
   * panel, so every move postpones the switch.
   */
  #pointerMoveListener = () => {
    if (!this.#pendingSwitch) return;

    if (this.#switchTimeout !== null) clearTimeout(this.#switchTimeout);
    this.#switchTimeout = setTimeout(this.#commitPendingSwitch, SWITCH_REST);
  };

  /**
   * Open the queued panel, provided the pointer is still resting on it.
   */
  #commitPendingSwitch = () => {
    const pending = this.#pendingSwitch;
    if (!pending) return;

    const { target } = pending;
    this.#clearPendingSwitch();

    if (!target.matches(':hover')) return;

    const item = findMenuItem(target);
    if (!item || item == this.#state.activeItem) return;

    this.#activateNow(target, item);
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#scheduleDeactivate();
  };

  /**
   * The pointer left the menu altogether — panel, nav row and all.
   */
  #hostLeaveListener = () => {
    this.#clearPendingSwitch();
    this.#scheduleDeactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item. Opening from a closed state and keyboard focus are immediate;
   * switching away from an already open panel waits for the pointer to come to rest, so it can
   * travel across neighbouring top-level links to reach the open panel.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // Any activity over the menu cancels a queued close.
    this.#clearDeactivateTimeout();

    const item = findMenuItem(event.target);

    if (!item) return;

    // Pointer returned to the item that is already open — drop any queued switch.
    this.#clearPendingSwitch();

    if (item == this.#state.activeItem) return;

    const { target } = event;

    // Moving between items inside the open "More" panel is a deliberate vertical move, not a
    // transit across the header row, so it stays instant.
    const isTransitRisk = event.type === 'pointerenter' && target.slot !== 'overflow';

    if (!isTransitRisk || !this.#state.activeItem) {
      this.#activateNow(target, item);
      return;
    }

    this.#pendingSwitch = { target };
    document.addEventListener('pointermove', this.#pointerMoveListener);
    this.#switchTimeout = setTimeout(this.#commitPendingSwitch, SWITCH_REST);
  };

  /**
   * Activate a menu item immediately.
   * @param {Element} target - The menu list item the activation originated from.
   * @param {HTMLElement} item - The menu link to expand.
   */
  #activateNow = (target, item) => {
    if (!this.headerComponent) return;

    const isDefaultSlot = target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      // Switching straight from one panel to another bypasses `#deactivate`, so retire the outgoing
      // panel's active flag here or its content-visibility optimization stays disabled.
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // The pointer moved on before settling on the item it was hovering, so drop its queued switch.
    if (this.#pendingSwitch?.target === event.target) this.#clearPendingSwitch();

    // Keyboard (`blur`) closes immediately; a pointer leaving gets a grace period so it can cross
    // the header row on its way into the open panel.
    if (event.type === 'pointerleave') {
      this.#scheduleDeactivate();
    } else {
      this.#deactivate();
    }
  }

  /**
   * Queue a deactivation of the currently active item, cancelled by any re-entry into the menu.
   */
  #scheduleDeactivate = () => {
    const item = this.#state.activeItem;
    if (!item) return;

    this.#clearDeactivateTimeout();
    this.#deactivateTimeout = setTimeout(() => {
      this.#deactivateTimeout = null;

      // Leaving one nav link for the gap beside it, or for the open panel, also fires `pointerleave`.
      // `:hover` covers this host's whole subtree, so if it still matches the pointer never actually
      // left the menu — closing here is what let a re-entry reopen the wrong panel. A genuine exit
      // fires `pointerleave` on the host and schedules this again.
      if (this.matches(':hover')) return;

      this.#deactivate(item);
    }, CLOSE_DELAY);
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
